import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';
import { getItemTitle, getItemDeepLink } from '../../../itemRef.js';

export const conversationMixin = {

    //********************* OPEN CONVERSATION ********************************//
    // Loads (creating if needed) the 1:1 GudHub Messenger conversation with
    // otherUserId, renders it, and subscribes to live updates for new messages.
    // * otherUserId (string|number) — GudHub user id of the DM partner
    // * itemContext (Object|null) — { app_id, view_id, item_id } | null
    async openConversation(otherUserId, itemContext = null) {
        this.unsubscribeFromConversation();

        this.activeUserId = String(gudhub.storage.user.user_id);
        this.itemContext = itemContext;

        this.render(`<div class="chat_loader loader"><span></span></div>`);

        const otherUserIdStr = String(otherUserId);
        const otherUser = await gudhub.getUserById(otherUserIdStr);
        const { conversationId, isNew } = await this.findOrCreateConversation(otherUserIdStr);
        const messages = await this.loadMessages(conversationId);

        this.participantsCache = new Map([
            [this.activeUserId, gudhub.storage.user],
            [otherUserIdStr, otherUser]
        ]);

        await this.prefetchThreadParticipants(messages);

        this.viewMode = 'dm';
        this.dmState = { conversationId, otherUserId: otherUserIdStr, otherUser, messages };
        this.threadState = null;
        this.channelState = null;
        this.conversationId = conversationId;

        if(this.itemContext) {
            await this.checkOtherUserAppAccess(this.itemContext.app_id);
        }

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();

        if(isNew) {
            this.dispatchEvent(new CustomEvent('direct_conversation_created', {
                bubbles: true,
                composed: true,
                detail: { user_id: otherUserIdStr }
            }));
        }
    },

    //********************* OPEN CHANNEL ********************************//
    // Loads a channel conversation, renders it, and subscribes to live updates.
    // * conversationId (string) — conversation_id of the channel
    // * name (string) — channel name (used as fallback if the conversation lacks one)
    async openChannel(conversationId, name) {
        this.unsubscribeFromConversation();

        this.activeUserId = String(gudhub.storage.user.user_id);

        this.render(`<div class="chat_loader loader"><span></span></div>`);

        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${conversationId}?user_id=${this.activeUserId}`);
        const conversation = await response.json();

        const messages = conversation.messages.map(message => this.normalizeMessage(message));

        this.participantsCache = this.participantsCache || new Map([[this.activeUserId, gudhub.storage.user]]);
        await this.prefetchParticipants(conversation.participants);
        await this.prefetchThreadParticipants(messages);

        this.viewMode = 'channel';
        this.channelState = { conversationId, name: conversation.name || name, participants: conversation.participants.map(String), messages };
        this.dmState = null;
        this.threadState = null;
        this.conversationId = conversationId;

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* OPEN MESSAGE THREAD ********************************//
    // "Reply in thread" - opens (creating if needed) the message-thread anchored
    // to this message, replacing the current dm/item-thread view with the thread
    // view ("← Back" returns to wherever it was opened from, see backToParentView).
    // * messageId (string) — message_id of the message being threaded
    async openMessageThread(messageId) {
        const message = this.activeMessages().find(m => m.message_id === messageId);

        if(!message.thread_id) {
            const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${this.conversationId}/thread`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message_id: messageId, created_by: this.activeUserId })
            });

            const thread = await response.json();
            message.thread_id = thread.conversation_id;
        }

        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${message.thread_id}`);
        const conversation = await response.json();

        await this.prefetchParticipants(conversation.participants);

        this.unsubscribeFromConversation();

        const parentView = this.viewMode === 'dm' ? { kind: 'dm' } : this.viewMode === 'channel' ? { kind: 'channel' } : { kind: 'thread', threadState: this.threadState };

        this.threadState = {
            conversationId: conversation.conversation_id,
            kind: 'message',
            parentMessage: message,
            parentView,
            participants: conversation.participants,
            messages: conversation.messages.map(m => this.normalizeMessage(m))
        };

        this.viewMode = 'thread';
        this.conversationId = this.threadState.conversationId;

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* SHOW NO DISCUSSION ********************************//
    // The item page's empty state, shown when the current item has no
    // discussion thread yet and the user hasn't opened a dm/channel (see
    // gh-users-side-bar's showNoDiscussionState). Renders a placeholder with
    // a composer - sendMessage() creates the item-thread the first time the
    // user sends something (participants default to solo, see
    // getParticipantIds() and openItemConversation).
    // * itemContext (Object) — { app_id, view_id, item_id }
    async showNoDiscussion(itemContext) {
        this.unsubscribeFromConversation();

        this.activeUserId = String(gudhub.storage.user.user_id);
        this.itemContext = itemContext;
        this.participantsCache = this.participantsCache || new Map([[this.activeUserId, gudhub.storage.user]]);
        this.dmState = null;
        this.channelState = null;
        this.threadState = null;
        this.conversationId = null;
        this.viewMode = 'no_discussion';

        this.renderActiveConversation();
    },

    //********************* OPEN ITEM CONVERSATION ********************************//
    // "Discuss this item" button in the dm header - finds or creates the shared
    // item-thread for the current item, inviting the dm partner, and opens the
    // thread view ("← Back" returns to the dm).
    async openItemConversation() {
        const { app_id, item_id, view_id } = this.itemContext;

        const participants = this.getParticipantIds();

        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/item`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                app_id, item_id, view_id,
                participants,
                created_by: this.activeUserId
            })
        });

        const conversation = await response.json();

        this.dispatchEvent(new CustomEvent('item_thread_discussed', {
            bubbles: true,
            composed: true,
            detail: {
                conversation_id: conversation.conversation_id,
                item_ref: conversation.item_ref,
                unread_count: 0,
                participants: conversation.participants.map(String)
            }
        }));

        await this.enterItemThread(conversation);
    },

    //********************* OPEN ITEM DISCUSSION ********************************//
    // Sidebar-driven entry into an item-thread - no dm to return to ("← Back" hidden).
    // * conversationId (string) — conversation_id of the item-thread
    // * itemRef (Object) — { app_id, item_id, view_id }, used as a fallback if the
    //   conversation document is missing item_ref
    async openItemDiscussion(conversationId, itemRef) {
        this.activeUserId = String(gudhub.storage.user.user_id);
        this.participantsCache = this.participantsCache || new Map([[this.activeUserId, gudhub.storage.user]]);
        this.dmState = null;
        this.channelState = null;

        const conversation = await this.fetchConversation(conversationId);

        conversation.item_ref = conversation.item_ref || itemRef;

        await this.enterItemThread(conversation);
    },

    //********************* ENTER ITEM THREAD ********************************//
    // Shared by openItemConversation/openItemDiscussion - builds threadState for
    // kind 'item' from a conversation document and switches into thread view.
    // * conversation (Object) — conversation document with `item_ref` set
    async enterItemThread(conversation) {
        const { app_id, item_id, view_id } = conversation.item_ref;
        const messages = conversation.messages.map(m => this.normalizeMessage(m));

        const [itemTitle] = await Promise.all([
            getItemTitle(app_id, item_id),
            this.prefetchParticipants(conversation.participants).then(() => this.prefetchThreadParticipants(messages))
        ]);

        this.unsubscribeFromConversation();

        this.threadState = {
            conversationId: conversation.conversation_id,
            kind: 'item',
            itemRef: conversation.item_ref,
            itemTitle,
            itemDeepLink: getItemDeepLink(app_id, view_id, item_id),
            participants: conversation.participants,
            messages
        };

        this.viewMode = 'thread';
        this.conversationId = this.threadState.conversationId;

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* OPEN DISCUSSED ITEM ********************************//
    // Inline item-discussion card (chat timeline) - opens an item-thread from
    // the current dm/channel without clearing dmState/channelState, so "← Back
    // to direct message"/"← Back to channel" still returns here (unlike
    // openItemDiscussion, the sidebar-driven entry point).
    // * conversationId (string) — conversation_id of the item-thread
    // * itemRef (Object) — { app_id, item_id, view_id }, used as a fallback if the
    //   conversation document is missing item_ref
    async openDiscussedItem(conversationId, itemRef) {
        const conversation = await this.fetchConversation(conversationId);

        conversation.item_ref = conversation.item_ref || itemRef;

        await this.enterItemThread(conversation);
    },

    //********************* BACK TO DIRECT MESSAGE ********************************//
    // Returns from a thread view to the dm it was opened from. Only reachable
    // when dmState is set (i.e. not a sidebar-opened item-thread).
    async backToDirectMessage() {
        if(!this.dmState) return;

        this.unsubscribeFromConversation();

        this.dmState.messages = await this.loadMessages(this.dmState.conversationId);
        await this.prefetchThreadParticipants(this.dmState.messages);
        this.threadState = null;
        this.viewMode = 'dm';
        this.conversationId = this.dmState.conversationId;

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* BACK TO CHANNEL ********************************//
    // Returns from a thread view (item-discussion or message-thread) to the
    // channel it was opened from. Only reachable when channelState is set.
    async backToChannel() {
        if(!this.channelState) return;

        this.unsubscribeFromConversation();

        this.channelState.messages = await this.loadMessages(this.channelState.conversationId);
        await this.prefetchThreadParticipants(this.channelState.messages);
        this.threadState = null;
        this.viewMode = 'channel';
        this.conversationId = this.channelState.conversationId;

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* BACK TO PARENT VIEW ********************************//
    // Returns from a message-thread ("Reply in thread") to the dm or
    // item-thread it was opened from (see openMessageThread's parentView).
    async backToParentView() {
        const { parentView } = this.threadState;

        this.unsubscribeFromConversation();

        if(parentView.kind === 'dm') {
            this.dmState.messages = await this.loadMessages(this.dmState.conversationId);
            await this.prefetchThreadParticipants(this.dmState.messages);
            this.threadState = null;
            this.viewMode = 'dm';
            this.conversationId = this.dmState.conversationId;
        } else if(parentView.kind === 'channel') {
            this.channelState.messages = await this.loadMessages(this.channelState.conversationId);
            await this.prefetchThreadParticipants(this.channelState.messages);
            this.threadState = null;
            this.viewMode = 'channel';
            this.conversationId = this.channelState.conversationId;
        } else {
            this.threadState = parentView.threadState;
            this.threadState.messages = await this.loadMessages(this.threadState.conversationId);
            await this.prefetchThreadParticipants(this.threadState.messages);
            this.viewMode = 'thread';
            this.conversationId = this.threadState.conversationId;
        }

        this.renderActiveConversation();
        this.subscribeToConversation();
        this.markAsRead();
    },

    //********************* SET ITEM CONTEXT ********************************//
    // Called by gudhub-messenger on $routeChangeSuccess. If currently viewing an
    // item-thread for a *different* item while a dm is open, falls back to the
    // dm. Otherwise just refreshes the "Discuss this item" button.
    // * itemContext (Object|null) — { app_id, view_id, item_id } | null
    async setItemContext(itemContext) {
        const previousItemContext = this.itemContext;
        this.itemContext = itemContext;

        const current = this.threadState?.itemRef;
        const sameItem = !!(itemContext && current && itemContext.app_id === current.app_id && itemContext.item_id === current.item_id);

        if(this.viewMode === 'thread' && this.threadState?.kind === 'item' && !sameItem) {
            if(this.dmState) {
                await this.backToDirectMessage();
                return;
            }

            if(this.channelState) {
                await this.backToChannel();
                return;
            }

            // Standalone item-thread (opened via the sidebar or the "no
            // discussion yet" composer, with no dm/channel underneath to fall
            // back to) - clear and hide it so the previous item's discussion
            // doesn't linger on screen while gh-users-side-bar's own
            // setItemContext (called right after this, see gh_conversation.js's
            // updateItemContext) decides what replaces it: this item's own
            // thread, or the "no discussion yet" composer (see
            // applyItemDiscussionState/showNoDiscussionState).
            this.unsubscribeFromConversation();
            this.threadState = null;
            this.viewMode = null;
            this.conversationId = null;
            this.classList.add('hidden');
            return;
        }

        const samePreviousItem = !!(itemContext && previousItemContext && itemContext.app_id === previousItemContext.app_id && itemContext.item_id === previousItemContext.item_id);

        if(this.viewMode === 'no_discussion' && !samePreviousItem) {
            // Same reasoning as above - the empty-state composer is tied to
            // whatever item was in context when showNoDiscussion() rendered
            // it, so it's just as stale once the item changes.
            this.unsubscribeFromConversation();
            this.viewMode = null;
            this.conversationId = null;
            this.classList.add('hidden');
            return;
        }

        if(this.viewMode === 'dm' || this.viewMode === 'channel') {
            if(this.viewMode === 'dm' && itemContext) {
                const contextBeforeCheck = this.itemContext;
                await this.checkOtherUserAppAccess(itemContext.app_id);
                if(this.itemContext !== contextBeforeCheck) return;
            }
            this.renderDiscussButton();
        }
    },

    //********************* OPEN ITEM ********************************//
    // "Open item" header button (thread kind 'item') - navigates the app to this
    // item's open_item route, same pattern as gh-users-side-bar's selectItemDiscussion.
    openItem() {
        const { app_id, view_id, item_id } = this.threadState.itemRef;

        const $location = gudhub.ghconstructor.angularInjector.get('$location');
        const $rootScope = gudhub.ghconstructor.angularInjector.get('$rootScope');

        // On mobile the expanded panel covers the whole viewport, so changing
        // the route on its own is invisible: the item loads underneath and the
        // user is left looking at the same chat, as if the button did nothing.
        // Collapsing first is what makes the navigation observable - same
        // sequence as showAppsTab(), which has to solve exactly this.
        const messenger = this.closest('gudhub-messenger');

        if(messenger?.isMobile()) {
            messenger.setMobileTab('apps');
            messenger.setExpanded(false);
        }

        $location.path(getItemDeepLink(app_id, view_id, item_id));
        $rootScope.$apply();
    },

    //********************* NAVIGATE BACK ********************************//
    navigateBack() {
        this.closest('gudhub-messenger')?.navigateBack();
    },

    //********************* CLOSE CONVERSATION ********************************//
    // Unsubscribes from live updates and clears local state so the next
    // openConversation() call re-fetches everything from scratch.
    closeConversation() {
        this.unsubscribeFromConversation();
        this.conversationId = null;
        this.viewMode = null;
        this.dmState = null;
        this.threadState = null;
        this.channelState = null;
        this.itemContext = null;
        this._otherUserHasAppAccess = false;
    },
};
